import React, { useState } from 'react';
import { 
    User, Star, Target, Brain, Heart, Trophy, Clock, Sparkles,
    ChevronDown, ChevronUp, MapPin, Calendar, Briefcase, CheckCircle,
    TrendingUp, Users, Zap, BookOpen, MessageSquare, Award
} from 'lucide-react';
import FoundingBadge from './FoundingBadge';

/**
 * TalentCard Component - 8-Card Architecture Display
 * 
 * Always Visible:
 * - Header Card
 * - Snapshot Score Card
 * 
 * Expandable (Hidden by default):
 * - Skills & Strengths
 * - Microtest
 * - Problem Solving & Thinking Style
 * - Working Style & Preferences
 * - Achievements & Proof
 * - Trial Readiness
 */

const ScoreBar = ({ score, maxScore = 5, isLowConfidence = false }) => {
    const percentage = (score / maxScore) * 100;
    return (
        <div className="flex items-center space-x-2">
            <div className={`flex-1 h-2 rounded-full overflow-hidden ${isLowConfidence ? 'bg-gray-100' : 'bg-gray-200'}`}>
                <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${percentage}%`, backgroundColor: isLowConfidence ? '#CBD5E1' : '#14B8A6' }}
                />
            </div>
            <span className={`text-sm font-bold w-8 ${isLowConfidence ? 'opacity-40' : ''}`} style={{ color: isLowConfidence ? '#94A3B8' : '#14B8A6' }}>{score}</span>
        </div>
    );
};

const ExpandableCard = ({ title, icon: Icon, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    
    return (
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
                <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F3E8FF' }}>
                        <Icon className="h-5 w-5" style={{ color: '#9333EA' }} />
                    </div>
                    <span className="font-semibold text-gray-800">{title}</span>
                </div>
                {isOpen ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
            </button>
            {isOpen && (
                <div className="px-4 pb-4 pt-0 border-t border-gray-100">
                    {children}
                </div>
            )}
        </div>
    );
};

const TalentCard = ({ talentCard, showFullCard = false, candidateName = null, viewMode = 'employer' }) => {
    // State for tag click interaction - must be before any returns
    const [expandedCard, setExpandedCard] = useState(null);
    // Tracks which skill-dimension evidence boxes are expanded, keyed by dimension key
    const [expandedEvidence, setExpandedEvidence] = useState({});
    const isCandidateView = viewMode === 'candidate';
    
    if (!talentCard) {
        return (
            <div className="text-center py-12">
                <Sparkles className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No Talent Card available yet.</p>
            </div>
        );
    }

    // Normalize talent card data to handle both old and new formats
    const normalizeCard = (card) => {
        // Check if it's the old format by looking for old-style keys
        const isOldFormat = !card.header?.primary_role && (card.candidate_overview || card.skills_evaluation || card.micro_test_result);
        
        if (!isOldFormat) {
            return card; // New format, use as-is
        }
        
        // Convert old format to new format
        return {
            header: card.header || {
                name: candidateName || 'Candidate',
                primary_role: 'Assessment Completed',
                location: 'Not specified',
                sector: null
            },
            snapshot_score: {
                skill_dimensions: {
                    execution_delivery: { score: card.overall_fit_score?.fit_score ? Math.min(card.overall_fit_score.fit_score / 20, 5) : 3, evidence: 'Based on previous assessment' },
                    communication: { score: 3, evidence: 'Based on previous assessment' },
                    problem_solving: { score: 3, evidence: 'Based on previous assessment' },
                    ownership_initiative: { score: 3, evidence: 'Based on previous assessment' },
                    learning_speed: { score: 3, evidence: 'Based on previous assessment' },
                    collaboration: { score: 3, evidence: 'Based on previous assessment' }
                },
                key_strengths: card.unique_strengths || card.skills_evaluation?.demonstrated_skills || ['Assessment pending'],
                trial_signals: {
                    readiness: 'Yes',
                    confidence: 'Medium'
                },
                snapshot_summary: card.recommendation || 'Assessment completed - upgraded format pending.'
            },
            skills_strengths: {
                short_view: {
                    top_skills: (Array.isArray(card.unique_strengths) ? card.unique_strengths : []).slice(0, 3),
                    summary: card.skills_evaluation?.overall_assessment || 'Skills assessment completed'
                },
                expanded_view: {
                    ranked_skills: (Array.isArray(card.unique_strengths) ? card.unique_strengths : []).map(skill => ({
                        skill: skill,
                        proficiency: 'Assessed',
                        evidence: 'From previous assessment'
                    })),
                    growth_areas: card.skills_evaluation?.areas_for_improvement || []
                }
            },
            microtest: {
                short_view: {
                    status: card.micro_test_result?.status || 'Completed',
                    observed_strengths: card.micro_test_result?.strengths || []
                },
                expanded_view: {
                    thinking_approach: card.micro_test_result?.approach || 'Under review',
                    execution_clarity: card.micro_test_result?.quality || 'Under review'
                }
            },
            problem_solving: {
                short_view: {
                    thinking_tags: ['Assessment pending']
                },
                expanded_view: {
                    ambiguity_handling: 'Under review',
                    decision_making: 'Under review',
                    learning_approach: 'Under review'
                }
            },
            working_style: {
                short_view: {
                    summary: 'Working style assessment completed'
                },
                expanded_view: {
                    manager_fit: 'Under review',
                    environment_fit: 'Under review'
                }
            },
            achievements: {
                short_view: {
                    headline_achievements: Array.isArray(card.achievements_snapshot) ? card.achievements_snapshot.slice(0, 2) : ['Assessment completed']
                },
                expanded_view: {
                    achievements_detail: [],
                    credibility_summary: 'Under review'
                }
            },
            trial_readiness: {
                short_view: {
                    readiness_status: 'Ready',
                    timeline: typeof card.trial_readiness_summary === 'object' 
                        ? card.trial_readiness_summary.earliest_start || 'Flexible'
                        : 'Flexible',
                    location_flexibility: typeof card.trial_readiness_summary === 'object'
                        ? `${card.header?.location || 'Location not specified'} (${card.trial_readiness_summary.work_environment || 'Flexible'})`
                        : (card.header?.location || 'Flexible')
                },
                expanded_view: typeof card.trial_readiness_summary === 'object' ? {
                    availability_window: card.trial_readiness_summary.availability || 'Under review',
                    preferred_duration: card.trial_readiness_summary.preferred_trial_length || 'Flexible',
                    location_details: `Based in ${card.header?.location || 'location not specified'}`,
                    work_arrangement: card.trial_readiness_summary.work_environment || 'Flexible',
                    compensation_expectation: 'Not specified',
                    constraints_and_accommodations: 'N/A',
                    transition_notes: card.trial_readiness_summary.earliest_start || 'N/A'
                } : null,
                summary: typeof card.trial_readiness_summary === 'object'
                    ? `Available ${card.trial_readiness_summary.availability || 'for trial'} in ${card.header?.location || 'their location'}. Preferred duration: ${card.trial_readiness_summary.preferred_trial_length || 'Flexible'}.`
                    : (card.trial_readiness_summary || 'Available for trial'),
                availability_match: true,
                recommended_trial_type: typeof card.trial_readiness_summary === 'object'
                    ? card.trial_readiness_summary.preferred_trial_length || 'Standard'
                    : 'Standard'
            }
        };
    };
    
    const normalizedCard = normalizeCard(talentCard);
    const { header, snapshot_score, skills_strengths, microtest, problem_solving, working_style, achievements, trial_readiness } = normalizedCard;
    const strength_tags = normalizedCard.strength_tags || talentCard.strength_tags;
    
    // Dimension icons mapping
    const dimensionIcons = {
        execution_delivery: TrendingUp,
        communication: MessageSquare,
        problem_solving: Brain,
        ownership_initiative: Zap,
        learning_speed: BookOpen,
        collaboration: Users
    };
    
    const dimensionLabels = {
        execution_delivery: 'Execution & Delivery',
        communication: 'Communication',
        problem_solving: 'Problem Solving',
        ownership_initiative: 'Ownership & Initiative',
        learning_speed: 'Learning Speed',
        collaboration: 'Collaboration'
    };

    return (
        <div className="space-y-4">
            {/* CARD 1: HEADER (Always Visible) */}
            <div className="rounded-2xl p-6 border-2" style={{ backgroundColor: '#F3E8FF', borderColor: '#9333EA' }}>
                <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-4">
                        {header?.profile_photo_url ? (
                            <img 
                                src={header.profile_photo_url} 
                                alt={header?.name}
                                className="w-16 h-16 rounded-full object-cover border-2 border-white"
                            />
                        ) : (
                            <div className="w-16 h-16 rounded-full flex items-center justify-center bg-white">
                                <User className="h-8 w-8" style={{ color: '#9333EA' }} />
                            </div>
                        )}
                        <div>
                            <span className="inline-flex items-center gap-2">
                                <h2 className="text-2xl font-bold text-gray-800">{header?.name || 'Candidate'}</h2>
                                {header?.founding_member_number != null && (
                                    <FoundingBadge number={header.founding_member_number} />
                                )}
                            </span>
                            {(() => {
                                const cr = header?.current_role;
                                if (!cr || typeof cr !== 'object') return null;
                                let line = null;
                                if (cr.status === 'Looking for my first role') {
                                    line = 'Seeking first role';
                                } else if (cr.title) {
                                    const base = cr.company ? `${cr.title} at ${cr.company}` : cr.title;
                                    line = cr.years ? `${base} · ${cr.years}` : base;
                                }
                                if (!line) return null;
                                return (
                                    <p className="text-sm text-slate-500 mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="talent-card-role-line">
                                        {line}
                                    </p>
                                );
                            })()}
                            <div className="flex flex-wrap gap-2 mt-1">
                                <span className="px-3 py-1 rounded-full text-sm font-medium bg-white" style={{ color: '#14B8A6' }}>
                                    {header?.primary_role || 'Role not specified'}
                                </span>
                                {header?.secondary_role && (
                                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-white text-gray-600">
                                        {header.secondary_role}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    {header?.phase_badge && (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-purple-600 to-pink-500 text-white">
                            {header.phase_badge}
                        </span>
                    )}
                </div>
                
                <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-600">
                    {header?.sector && (
                        <span className="flex items-center">
                            <Briefcase className="h-4 w-4 mr-1" style={{ color: '#9333EA' }} />
                            {header.sector.charAt(0).toUpperCase() + header.sector.slice(1)}
                        </span>
                    )}
                    {header?.location && (
                        <span className="flex items-center">
                            <MapPin className="h-4 w-4 mr-1" style={{ color: '#9333EA' }} />
                            {header.location}
                        </span>
                    )}
                    {header?.work_preference && (
                        <span className="flex items-center">
                            <Target className="h-4 w-4 mr-1" style={{ color: '#9333EA' }} />
                            {header.work_preference}
                        </span>
                    )}
                    {header?.availability_start && (
                        <span className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" style={{ color: '#9333EA' }} />
                            Available: {header.availability_start}
                        </span>
                    )}
                </div>
            </div>

            {/* STRENGTH TAGS - Gaming-Style Visual — hidden in candidate view (shown as chips in profile block) */}
            {!isCandidateView && strength_tags && (strength_tags.top_tag || strength_tags.secondary_tag) && (
                <div className="rounded-2xl p-6 bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 shadow-sm">
                    <div className="flex flex-col items-center space-y-4">
                        {/* Top Tag - Large & Prominent */}
                        {strength_tags.top_tag && (
                            <div 
                                className="group cursor-pointer transform transition-all duration-300 hover:scale-105"
                                onClick={() => setExpandedCard(expandedCard === 'skills' ? null : 'skills')}
                            >
                                <div className="flex items-center space-x-3 px-6 py-3 rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg hover:shadow-xl transition-shadow">
                                    <span className="text-3xl">{strength_tags.top_tag.emoji}</span>
                                    <div>
                                        <h3 className="text-xl font-bold text-white">{strength_tags.top_tag.name}</h3>
                                        <p className="text-xs text-purple-200">Top Strength</p>
                                    </div>
                                    {strength_tags.top_tag.confidence_score && (
                                        <div className="ml-2 px-2 py-1 rounded-full bg-white/20 text-xs text-white font-medium">
                                            {strength_tags.top_tag.confidence_score.toFixed(1)}
                                        </div>
                                    )}
                                </div>
                                <p className="text-center text-sm text-gray-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {strength_tags.top_tag.description || 'Click to see evidence'}
                                </p>
                            </div>
                        )}
                        
                        {/* Secondary Tag - Smaller */}
                        {strength_tags.secondary_tag && (
                            <div 
                                className="group cursor-pointer transform transition-all duration-300 hover:scale-105"
                                onClick={() => setExpandedCard(expandedCard === 'skills' ? null : 'skills')}
                            >
                                <div className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-white border-2 border-purple-300 shadow-sm hover:shadow-md transition-shadow">
                                    <span className="text-xl">{strength_tags.secondary_tag.emoji}</span>
                                    <div>
                                        <h4 className="text-base font-semibold text-gray-800">{strength_tags.secondary_tag.name}</h4>
                                        <p className="text-xs text-gray-500">Secondary Strength</p>
                                    </div>
                                    {strength_tags.secondary_tag.confidence_score && (
                                        <div className="ml-2 px-2 py-0.5 rounded-full bg-purple-100 text-xs font-medium" style={{ color: '#9333EA' }}>
                                            {strength_tags.secondary_tag.confidence_score.toFixed(1)}
                                        </div>
                                    )}
                                </div>
                                <p className="text-center text-xs text-gray-500 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {strength_tags.secondary_tag.description || 'Click to see evidence'}
                                </p>
                            </div>
                        )}
                        
                        <p className="text-xs text-gray-400 mt-2">Tap tags to see detailed evidence</p>
                    </div>
                </div>
            )}

            {/* CARD 2: SNAPSHOT SCORE (Always Visible - Primary Decision Surface) */}
            <div className="rounded-2xl p-6 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center">
                        <Star className="h-5 w-5 mr-2" style={{ color: '#14B8A6' }} />
                        Snapshot Score
                    </h3>
                    {!isCandidateView && snapshot_score?.trial_signals?.confidence && (
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            snapshot_score.trial_signals.confidence === 'High' 
                                ? 'bg-green-100 text-green-700'
                                : snapshot_score.trial_signals.confidence === 'Medium'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-gray-100 text-gray-600'
                        }`}>
                            {snapshot_score.trial_signals.confidence} Confidence
                        </span>
                    )}
                </div>

                {/* Snapshot Summary */}
                {snapshot_score?.snapshot_summary && (
                    <div className="p-4 rounded-lg mb-6" style={{ backgroundColor: '#F0FDF4' }}>
                        <p className="text-gray-700 font-medium">{snapshot_score.snapshot_summary}</p>
                    </div>
                )}

                {/* Skill Dimensions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {snapshot_score?.skill_dimensions && Object.entries(snapshot_score.skill_dimensions).map(([key, value]) => {
                        const DimIcon = dimensionIcons[key] || Star;
                        return (
                            <div key={key} className={`p-3 rounded-lg ${value?.confidence === 'low' ? 'bg-gray-50/60' : 'bg-gray-50'}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className={`flex items-center text-sm font-medium ${value?.confidence === 'low' ? 'text-gray-400' : 'text-gray-700'}`}>
                                        <DimIcon className={`h-4 w-4 mr-2 ${value?.confidence === 'low' ? 'opacity-40' : ''}`} style={{ color: '#9333EA' }} />
                                        {dimensionLabels[key] || key}
                                    </span>
                                    {value?.confidence === 'low' && (
                                        <span className="text-[10px] text-slate-400 italic px-1.5 py-0.5 bg-slate-100 rounded-full">Low data</span>
                                    )}
                                </div>
                                <ScoreBar score={value?.score || 0} isLowConfidence={value?.confidence === 'low'} />
                                {value?.evidence && (() => {
                                    const isLong = value.evidence.length > 90;
                                    const isExpanded = !!expandedEvidence[key];
                                    return (
                                        <>
                                            <p
                                                className={`text-xs mt-1 ${isLong && !isExpanded ? 'line-clamp-2' : ''} ${value?.confidence === 'low' ? 'text-gray-400 italic' : 'text-gray-500'}`}
                                                data-testid={`evidence-text-${key}`}
                                            >
                                                {value.evidence}
                                            </p>
                                            {isLong && (
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandedEvidence(prev => ({ ...prev, [key]: !prev[key] }))}
                                                    className="mt-1 flex items-center gap-0.5 text-[11px] font-semibold text-purple-600 hover:text-purple-800"
                                                    data-testid={`evidence-toggle-${key}`}
                                                >
                                                    {isExpanded ? (
                                                        <>Show less <ChevronUp className="h-3 w-3" /></>
                                                    ) : (
                                                        <>Show more <ChevronDown className="h-3 w-3" /></>
                                                    )}
                                                </button>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        );
                    })}
                </div>

                {/* Key Strengths */}
                {snapshot_score?.key_strengths && snapshot_score.key_strengths.length > 0 && (
                    <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-600 mb-2">Key Strengths</h4>
                        <div className="flex flex-wrap gap-2">
                            {snapshot_score.key_strengths.map((strength, idx) => (
                                <span 
                                    key={idx}
                                    className="px-3 py-1 rounded-full text-sm font-medium"
                                    style={{ backgroundColor: '#F3E8FF', color: '#9333EA' }}
                                >
                                    {strength}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Trial Signals */}
                {snapshot_score?.trial_signals && (
                    <div className={`grid ${isCandidateView ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-4'} gap-3 p-4 rounded-lg`} style={{ backgroundColor: '#F3E8FF' }}>
                        <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Availability</p>
                            <p className="font-bold" style={{ color: '#14B8A6' }}>
                                {snapshot_score.trial_signals.readiness || 'TBD'}
                            </p>
                        </div>
                        <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Preferred Duration</p>
                            <p className="font-bold text-gray-700">
                                {snapshot_score.trial_signals.preferred_length || 'Flexible'}
                            </p>
                        </div>
                        <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">Microtest</p>
                            <p className="font-bold text-gray-700">
                                {snapshot_score.trial_signals.microtest_status || 'Pending'}
                            </p>
                        </div>
                        {!isCandidateView && (
                            <div className="text-center">
                                <p className="text-xs text-gray-500 mb-1">Confidence</p>
                                <p className="font-bold" style={{ color: '#9333EA' }}>
                                    {snapshot_score.trial_signals.confidence || 'Emerging'}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* EXPANDABLE CARDS (Cards 3-8) */}
            {showFullCard && (
                <div className="space-y-3">
                    {/* Card 3: Skills & Strengths */}
                    <ExpandableCard title="Skills & Strengths" icon={Target}>
                        <div className="pt-4 space-y-4">
                            {skills_strengths?.short_view && (
                                <div>
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        {skills_strengths.short_view.top_skills?.map((skill, idx) => (
                                            <span key={idx} className="px-3 py-1 rounded-full text-sm font-bold bg-gray-100 text-gray-700">
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                    <p className="text-gray-600">{skills_strengths.short_view.summary}</p>
                                </div>
                            )}
                            {skills_strengths?.expanded_view?.ranked_skills && (
                                <div className="space-y-2 mt-4">
                                    <h5 className="font-semibold text-gray-700">Skills with Evidence</h5>
                                    <p className="text-xs text-gray-500 mb-2">Evidence pulled from your Achievements, Problem Solving, Microtest, and Working Style responses</p>
                                    {skills_strengths.expanded_view.ranked_skills.map((skill, idx) => (
                                        <div key={idx} className="p-3 rounded-lg bg-gray-50 border-l-4" style={{ borderColor: '#14B8A6' }}>
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="font-bold text-gray-800">{skill.skill}</span>
                                                <div className="flex items-center space-x-2">
                                                    {skill.evidence_source && (
                                                        <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: '#F3E8FF', color: '#9333EA' }}>
                                                            {skill.evidence_source}
                                                        </span>
                                                    )}
                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                        skill.proficiency === 'High' ? 'bg-green-100 text-green-700' :
                                                        skill.proficiency === 'Medium' ? 'bg-amber-100 text-amber-700' :
                                                        'bg-gray-100 text-gray-600'
                                                    }`}>
                                                        {skill.proficiency}
                                                    </span>
                                                </div>
                                            </div>
                                            {skill.evidence && (
                                                <p className="text-sm text-gray-600 italic">"{skill.evidence}"</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {!isCandidateView && skills_strengths?.expanded_view?.growth_areas && skills_strengths.expanded_view.growth_areas.length > 0 && (
                                <div>
                                    <h5 className="font-semibold text-gray-700 mb-2">Growth Areas</h5>
                                    <ul className="list-disc list-inside text-gray-600 text-sm">
                                        {skills_strengths.expanded_view.growth_areas.map((area, idx) => (
                                            <li key={idx}>{area}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </ExpandableCard>

                    {/* Card 4: Microtest */}
                    <ExpandableCard title="Microtest Results" icon={Sparkles}>
                        <div className="pt-4 space-y-4">
                            {!isCandidateView && microtest?.short_view && (
                                <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#F0FDF4' }}>
                                    <span className="font-medium text-gray-700">Status:</span>
                                    <span className="font-bold" style={{ color: '#14B8A6' }}>{microtest.short_view.status}</span>
                                </div>
                            )}
                            {microtest?.short_view?.observed_strengths && (
                                <div>
                                    <h5 className="font-semibold text-gray-700 mb-2">Observed Strengths</h5>
                                    <div className="flex flex-wrap gap-2">
                                        {microtest.short_view.observed_strengths.map((strength, idx) => (
                                            <span key={idx} className="px-3 py-1 rounded-full text-sm font-bold bg-gray-100 text-gray-700">
                                                {strength}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {microtest?.expanded_view && (
                                <div className="space-y-3">
                                    {microtest.expanded_view.thinking_approach && (
                                        <div>
                                            <h5 className="text-sm font-semibold text-gray-600">Thinking Approach</h5>
                                            <p className="text-gray-700">{microtest.expanded_view.thinking_approach}</p>
                                        </div>
                                    )}
                                    {microtest.expanded_view.execution_clarity && (
                                        <div>
                                            <h5 className="text-sm font-semibold text-gray-600">Execution Clarity</h5>
                                            <p className="text-gray-700">{microtest.expanded_view.execution_clarity}</p>
                                        </div>
                                    )}
                                    {microtest.expanded_view.learning_signals && (
                                        <div>
                                            <h5 className="text-sm font-semibold text-gray-600">Learning Signals</h5>
                                            <p className="text-gray-700">{microtest.expanded_view.learning_signals}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </ExpandableCard>

                    {/* Card 5: Problem Solving */}
                    <ExpandableCard title="Problem Solving & Thinking Style" icon={Brain}>
                        <div className="pt-4 space-y-4">
                            {problem_solving?.short_view?.thinking_tags && (
                                <div className="flex flex-wrap gap-2">
                                    {problem_solving.short_view.thinking_tags.map((tag, idx) => (
                                        <span 
                                            key={idx}
                                            className="px-3 py-1 rounded-full text-sm font-medium"
                                            style={{ backgroundColor: '#F3E8FF', color: '#9333EA' }}
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {problem_solving?.expanded_view && (
                                <div className="space-y-3">
                                    {problem_solving.expanded_view.ambiguity_handling && (
                                        <div>
                                            <h5 className="text-sm font-semibold text-gray-600">Handling Ambiguity</h5>
                                            <p className="text-gray-700">{problem_solving.expanded_view.ambiguity_handling}</p>
                                        </div>
                                    )}
                                    {problem_solving.expanded_view.decision_making && (
                                        <div>
                                            <h5 className="text-sm font-semibold text-gray-600">Decision Making</h5>
                                            <p className="text-gray-700">{problem_solving.expanded_view.decision_making}</p>
                                        </div>
                                    )}
                                    {problem_solving.expanded_view.learning_approach && (
                                        <div>
                                            <h5 className="text-sm font-semibold text-gray-600">Learning Approach</h5>
                                            <p className="text-gray-700">{problem_solving.expanded_view.learning_approach}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </ExpandableCard>

                    {/* Card 6: Working Style */}
                    <ExpandableCard title="Working Style & Preferences" icon={Heart}>
                        <div className="pt-4 space-y-4">
                            {/* Short View */}
                            {working_style?.short_view && (
                                <div className="space-y-3">
                                    {working_style.short_view.feedback_style && (
                                        <div className="flex items-start space-x-3 p-3 rounded-lg" style={{ backgroundColor: '#F0FDF4' }}>
                                            <span className="text-sm font-semibold text-gray-600 w-32 flex-shrink-0">Feedback Style:</span>
                                            <p className="text-gray-700">{working_style.short_view.feedback_style}</p>
                                        </div>
                                    )}
                                    {working_style.short_view.environment_fit && (
                                        <div className="flex items-start space-x-3 p-3 rounded-lg" style={{ backgroundColor: '#F3E8FF' }}>
                                            <span className="text-sm font-semibold text-gray-600 w-32 flex-shrink-0">Environment Fit:</span>
                                            <p className="text-gray-700">{working_style.short_view.environment_fit}</p>
                                        </div>
                                    )}
                                    {working_style.short_view.motivation_summary && (
                                        <div className="flex items-start space-x-3 p-3 rounded-lg" style={{ backgroundColor: '#F0FDF4' }}>
                                            <span className="text-sm font-semibold text-gray-600 w-32 flex-shrink-0">Motivation:</span>
                                            <p className="text-gray-700 font-medium">{working_style.short_view.motivation_summary}</p>
                                        </div>
                                    )}
                                    {/* Fallback for old format */}
                                    {working_style.short_view.summary && !working_style.short_view.feedback_style && (
                                        <div className="p-3 rounded-lg" style={{ backgroundColor: '#F0FDF4' }}>
                                            <p className="text-gray-700 font-medium">{working_style.short_view.summary}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Expanded View */}
                            {working_style?.expanded_view && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                                    {working_style.expanded_view.workday_structure && (
                                        <div className="p-3 rounded-lg bg-gray-50">
                                            <h5 className="text-sm font-semibold text-gray-600 mb-1">Workday Structure</h5>
                                            <p className="text-sm text-gray-700">{working_style.expanded_view.workday_structure}</p>
                                        </div>
                                    )}
                                    {working_style.expanded_view.manager_style && (
                                        <div className="p-3 rounded-lg bg-gray-50">
                                            <h5 className="text-sm font-semibold text-gray-600 mb-1">Manager Style</h5>
                                            <p className="text-sm text-gray-700">{working_style.expanded_view.manager_style}</p>
                                        </div>
                                    )}
                                    {working_style.expanded_view.team_fit && (
                                        <div className="p-3 rounded-lg bg-gray-50">
                                            <h5 className="text-sm font-semibold text-gray-600 mb-1">Team Fit</h5>
                                            <p className="text-sm text-gray-700">{working_style.expanded_view.team_fit}</p>
                                        </div>
                                    )}
                                    {working_style.expanded_view.next_role_intent && (
                                        <div className="p-3 rounded-lg bg-gray-50">
                                            <h5 className="text-sm font-semibold text-gray-600 mb-1">Next Role Intent</h5>
                                            <p className="text-sm text-gray-700">{working_style.expanded_view.next_role_intent}</p>
                                        </div>
                                    )}
                                    {/* Fallback for old format fields */}
                                    {working_style.expanded_view.manager_fit && !working_style.expanded_view.manager_style && (
                                        <div className="p-3 rounded-lg bg-gray-50">
                                            <h5 className="text-sm font-semibold text-gray-600 mb-1">Manager Fit</h5>
                                            <p className="text-sm text-gray-700">{working_style.expanded_view.manager_fit}</p>
                                        </div>
                                    )}
                                    {working_style.expanded_view.environment_fit && !working_style.short_view?.environment_fit && (
                                        <div className="p-3 rounded-lg bg-gray-50">
                                            <h5 className="text-sm font-semibold text-gray-600 mb-1">Environment Fit</h5>
                                            <p className="text-sm text-gray-700">{working_style.expanded_view.environment_fit}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </ExpandableCard>

                    {/* Card 7: Achievements */}
                    <ExpandableCard title="Achievements & Proof" icon={Trophy}>
                        <div className="pt-4 space-y-4">
                            {/* Short View - Headline Achievements */}
                            {achievements?.short_view?.headline_achievements && (
                                <div className="space-y-2">
                                    {achievements.short_view.headline_achievements.map((achievement, idx) => (
                                        <div key={idx} className="flex items-start space-x-2">
                                            <Award className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#14B8A6' }} />
                                            <p className="text-gray-700 font-medium">{(achievement || '').replace(/reportedly\s*/gi, '')}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {/* Expanded View - Detailed Achievements */}
                            {achievements?.expanded_view?.achievements_detail && achievements.expanded_view.achievements_detail.length > 0 && (
                                <div className="space-y-3">
                                    <h5 className="font-semibold text-gray-700">Detailed Achievements</h5>
                                    {achievements.expanded_view.achievements_detail.map((item, idx) => (
                                        <div key={idx} className="p-4 rounded-lg bg-gray-50 border-l-4" style={{ borderColor: '#9333EA' }}>
                                            {item.headline && (
                                                <p className="font-semibold text-gray-800 mb-2">{(item.headline || '').replace(/reportedly\s*/gi, '')}</p>
                                            )}
                                            <div className="space-y-2 text-sm">
                                                <p className="text-gray-700">
                                                    <span className="font-medium" style={{ color: '#14B8A6' }}>Action:</span> {(item.action || '').replace(/reportedly\s*/gi, '')}
                                                </p>
                                                <p className="text-gray-600">
                                                    <span className="font-medium" style={{ color: '#14B8A6' }}>Outcome:</span> {(item.outcome || '').replace(/reportedly\s*/gi, '')}
                                                </p>
                                                {item.evidence_type && (
                                                    <p className="text-gray-500">
                                                        <span className="font-medium">Evidence:</span> {(item.evidence_type || '').replace(/reportedly\s*/gi, '')}
                                                    </p>
                                                )}
                                                {item.tangible_output && (
                                                    <p className="text-gray-500 italic">
                                                        <span className="font-medium">Still exists:</span> {(item.tangible_output || '').replace(/reportedly\s*/gi, '')}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {/* Credibility Summary — employer only */}
                            {!isCandidateView && (achievements?.expanded_view?.credibility_summary || achievements?.expanded_view?.credibility_signals) && (
                                <div className="p-3 rounded-lg" style={{ backgroundColor: '#F0FDF4' }}>
                                    <h5 className="text-sm font-semibold text-gray-600 mb-1 flex items-center">
                                        <CheckCircle className="h-4 w-4 mr-1" style={{ color: '#14B8A6' }} />
                                        Credibility Assessment
                                    </h5>
                                    <p className="text-sm text-gray-700">
                                        {achievements.expanded_view.credibility_summary || achievements.expanded_view.credibility_signals}
                                    </p>
                                </div>
                            )}
                        </div>
                    </ExpandableCard>

                    {/* Card 8: Trial Readiness */}
                    <ExpandableCard title="Availability" icon={Clock}>
                        <div className="pt-4 space-y-4">
                            {/* Short View - Quick Overview */}
                            {trial_readiness?.short_view && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <div className="p-3 rounded-lg text-center" style={{ backgroundColor: '#F0FDF4' }}>
                                        <p className="text-xs text-gray-500 mb-1">Status</p>
                                        <p className="font-bold" style={{ color: 
                                            trial_readiness.short_view.readiness_status === 'Ready' ? '#14B8A6' :
                                            trial_readiness.short_view.readiness_status === 'Soon' ? '#D97706' : '#6B7280'
                                        }}>
                                            {trial_readiness.short_view.readiness_status || 'Ready'}
                                        </p>
                                    </div>
                                    <div className="p-3 rounded-lg bg-gray-50 text-center">
                                        <p className="text-xs text-gray-500 mb-1">Timeline</p>
                                        <p className="font-medium text-gray-700 text-sm">{trial_readiness.short_view.timeline || 'Flexible'}</p>
                                    </div>
                                    <div className="p-3 rounded-lg bg-gray-50 text-center">
                                        <p className="text-xs text-gray-500 mb-1">Location</p>
                                        <p className="font-medium text-gray-700 text-sm">{trial_readiness.short_view.location_flexibility || 'Flexible'}</p>
                                    </div>
                                </div>
                            )}
                            
                            {/* Summary */}
                            {trial_readiness?.summary && (
                                <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0FDF4' }}>
                                    <p className="text-gray-700 font-medium">{trial_readiness.summary}</p>
                                </div>
                            )}
                            
                            {/* Expanded View - Detailed Information */}
                            {trial_readiness?.expanded_view && (
                                <div className="space-y-3">
                                    <h5 className="font-semibold text-gray-700">
                                        Availability Details
                                    </h5>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {trial_readiness.expanded_view.availability_window && (
                                            <div className="p-3 rounded-lg bg-gray-50">
                                                <p className="text-xs font-semibold text-gray-500 mb-1">Availability</p>
                                                <p className="text-sm text-gray-700">{trial_readiness.expanded_view.availability_window}</p>
                                            </div>
                                        )}
                                        {trial_readiness.expanded_view.preferred_duration && (
                                            <div className="p-3 rounded-lg bg-gray-50">
                                                <p className="text-xs font-semibold text-gray-500 mb-1">Preferred Duration</p>
                                                <p className="text-sm text-gray-700">{trial_readiness.expanded_view.preferred_duration}</p>
                                            </div>
                                        )}
                                        {trial_readiness.expanded_view.location_details && (
                                            <div className="p-3 rounded-lg bg-gray-50">
                                                <p className="text-xs font-semibold text-gray-500 mb-1">Location</p>
                                                <p className="text-sm text-gray-700">{trial_readiness.expanded_view.location_details}</p>
                                            </div>
                                        )}
                                        {trial_readiness.expanded_view.work_arrangement && (
                                            <div className="p-3 rounded-lg bg-gray-50">
                                                <p className="text-xs font-semibold text-gray-500 mb-1">Work Arrangement</p>
                                                <p className="text-sm text-gray-700">{trial_readiness.expanded_view.work_arrangement}</p>
                                            </div>
                                        )}
                                        {trial_readiness.expanded_view.compensation_expectation && (
                                            <div className="p-3 rounded-lg bg-gray-50">
                                                <p className="text-xs font-semibold text-gray-500 mb-1">Salary Expectation</p>
                                                <p className="text-sm text-gray-700">{trial_readiness.expanded_view.compensation_expectation}</p>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Constraints and Transition Notes */}
                                    {(trial_readiness.expanded_view.constraints_and_accommodations || trial_readiness.expanded_view.transition_notes) && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                                            {trial_readiness.expanded_view.constraints_and_accommodations && 
                                             trial_readiness.expanded_view.constraints_and_accommodations !== 'N/A' && (
                                                <div className="p-3 rounded-lg border border-amber-200" style={{ backgroundColor: '#FFFBEB' }}>
                                                    <p className="text-xs font-semibold text-amber-600 mb-1">Accommodations Needed</p>
                                                    <p className="text-sm text-gray-700">{trial_readiness.expanded_view.constraints_and_accommodations}</p>
                                                </div>
                                            )}
                                            {trial_readiness.expanded_view.transition_notes && (
                                                <div className="p-3 rounded-lg bg-gray-50">
                                                    <p className="text-xs font-semibold text-gray-500 mb-1">Transition Notes</p>
                                                    <p className="text-sm text-gray-700">{trial_readiness.expanded_view.transition_notes}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* Quick Stats - Employer only: Recommended Trial */}
                            {!isCandidateView && (
                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 rounded-lg bg-gray-50 text-center">
                                    <p className="text-xs text-gray-500">Availability Match</p>
                                    <p className="font-bold" style={{ color: trial_readiness?.availability_match ? '#14B8A6' : '#EF4444' }}>
                                        {trial_readiness?.availability_match ? 'Yes' : 'Review Needed'}
                                    </p>
                                </div>
                                <div className="p-3 rounded-lg bg-gray-50 text-center">
                                    <p className="text-xs text-gray-500">Recommended Trial</p>
                                    <p className="font-bold text-gray-700">{trial_readiness?.recommended_trial_type || 'Standard'}</p>
                                </div>
                            </div>
                            )}
                            
                            {/* Potential Flags — employer only */}
                            {!isCandidateView && trial_readiness?.potential_flags && trial_readiness.potential_flags.length > 0 && 
                             !(trial_readiness.potential_flags.length === 1 && trial_readiness.potential_flags[0] === '') && (
                                <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                                    <h5 className="text-sm font-semibold text-yellow-700 mb-2">Potential Considerations</h5>
                                    <ul className="list-disc list-inside text-sm text-yellow-700">
                                        {trial_readiness.potential_flags.filter(flag => flag && flag.trim() !== '').map((flag, idx) => (
                                            <li key={idx}>{flag}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </ExpandableCard>
                </div>
            )}
        </div>
    );
};

export default TalentCard;
